import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Users, Clock, Plus, Minus, Check, X, FileText, ListTodo, MessageSquare, MapPin, DollarSign, Loader2, CreditCard, Image, Building2, Info, Upload, ShoppingCart } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { eventsApi, directoryApi, paymentsApi, uploadsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTier } from '../context/TierContext';
import RestrictedOverlay from './ui/RestrictedOverlay';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';

const RECIPIENT_BANK = {
  accountName: '1 GLOBE ALLIANCE PRIVATE LIMITED',
  bankName: 'ICICI BANK',
  usd: { accountNumber: '195606000031', swiftCode: 'ICICINBBCTS' },
  inr: { accountNumber: '195605004266', ifscCode: 'ICIC00001956' },
};

const RAZORPAY_MAX_MINOR_UNITS = {
  USD: 500000,
};

const EventConnect = () => {
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [meetingRequests, setMeetingRequests] = useState([]);
  const [scheduledMeetings, setScheduledMeetings] = useState([]);
  const [myRegistration, setMyRegistration] = useState(null);

  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [showRegisterDialog, setShowRegisterDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [bankTransferLoading, setBankTransferLoading] = useState(false);
  const [paymentFormLoading, setPaymentFormLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [gstNumber, setGstNumber] = useState('');
  const [gstCompanyLegalName, setGstCompanyLegalName] = useState('');
  const [gstUserName, setGstUserName] = useState('');
  const [payerAccountName, setPayerAccountName] = useState('');
  const [payerBankName, setPayerBankName] = useState('');
  const [payerAccountLast4, setPayerAccountLast4] = useState('');
  const [transactionReference, setTransactionReference] = useState('');
  const [transferDate, setTransferDate] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [proofFileName, setProofFileName] = useState('');
  const bankProofInputRef = useRef(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [selectedFeeQuantities, setSelectedFeeQuantities] = useState({});
  const [delegateCart, setDelegateCart] = useState([]);
  const [showSponsorshipDialog, setShowSponsorshipDialog] = useState(false);
  const [showSponsorshipCartDialog, setShowSponsorshipCartDialog] = useState(false);
  const [showSponsorshipBankDialog, setShowSponsorshipBankDialog] = useState(false);
  const [showSponsorshipProofDialog, setShowSponsorshipProofDialog] = useState(false);
  const [sponsorshipSelections, setSponsorshipSelections] = useState({});
  const [sponsorshipCart, setSponsorshipCart] = useState([]);
  const [sponsorshipCheckout, setSponsorshipCheckout] = useState(null);
  const [sponsorshipCheckoutLoading, setSponsorshipCheckoutLoading] = useState(false);
  const [sponsorshipPendingPayment, setSponsorshipPendingPayment] = useState(null);
  const [sponsorshipProofLoading, setSponsorshipProofLoading] = useState(false);
  const [sponsorshipTransactionReference, setSponsorshipTransactionReference] = useState('');
  const [sponsorshipTransferDate, setSponsorshipTransferDate] = useState('');
  const [sponsorshipProofFile, setSponsorshipProofFile] = useState(null);
  const [sponsorshipProofFileName, setSponsorshipProofFileName] = useState('');
  const sponsorshipProofInputRef = useRef(null);

  const { member, user } = useAuth();
  const { hasRestriction, getRestrictionMessage } = useTier();
  const currentMemberId = member?.member_id;
  const memberCountry = (
    member?.country
    || user?.country
    || member?.company_country
    || ''
  ).trim();
  const isIndiaMember = ['india', 'in', 'bharat'].includes(memberCountry.toLowerCase());

  const [scheduleForm, setScheduleForm] = useState({
    memberId: '',
    date: '',
    time: '',
    duration: 30,
    message: ''
  });

  const [meetingNotes, setMeetingNotes] = useState({
    notes: '',
    todoItems: []
  });

  const [newTodo, setNewTodo] = useState('');

  useEffect(() => {
    fetchEvents();
    fetchMembers();
  }, []);

  useEffect(() => {
    if (selectedEvent) {
      fetchMeetingRequests();
      fetchScheduledMeetings();
      checkRegistration();
      fetchSponsorshipPayment();
      initializeFeeSelection(selectedEvent);
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (!selectedEvent?.event_id) return;
    const raw = sessionStorage.getItem(`event-cart-${selectedEvent.event_id}-${currentMemberId || 'guest'}`);
    if (!raw) {
      setDelegateCart([]);
      setSponsorshipCart([]);
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setDelegateCart(Array.isArray(parsed.delegateCart) ? parsed.delegateCart : []);
      setSponsorshipCart(Array.isArray(parsed.sponsorshipCart) ? parsed.sponsorshipCart : []);
    } catch (_) {
      setDelegateCart([]);
      setSponsorshipCart([]);
    }
  }, [selectedEvent?.event_id, currentMemberId]);

  useEffect(() => {
    if (!selectedEvent?.event_id) return;
    sessionStorage.setItem(
      `event-cart-${selectedEvent.event_id}-${currentMemberId || 'guest'}`,
      JSON.stringify({ delegateCart, sponsorshipCart })
    );
  }, [delegateCart, sponsorshipCart, selectedEvent?.event_id, currentMemberId]);

  const getDelegateFeeOptions = (event = selectedEvent) => {
    const fees = event?.fees || [];
    const delegateFees = fees.filter((fee) => fee.fee_type === 'delegate');
    if (delegateFees.length > 0) return delegateFees;
    return event ? [{
      id: 'default_delegate',
      name: 'Delegate Pass',
      price: event.delegate_fee ?? 0,
      fee_type: 'delegate'
    }] : [];
  };

  const initializeFeeSelection = (event = selectedEvent) => {
    const defaultSelection = {};
    getDelegateFeeOptions(event).forEach((fee, index) => {
      defaultSelection[fee.id] = index === 0 ? 1 : 0;
    });
    setSelectedFeeQuantities(defaultSelection);
  };

  const updateFeeQuantity = (feeId, quantity) => {
    setSelectedFeeQuantities((prev) => ({
      ...prev,
      [feeId]: Math.max(0, parseInt(quantity, 10) || 0),
    }));
  };

  const getSelectedFeeLines = (event = selectedEvent) =>
    getDelegateFeeOptions(event)
      .map((fee) => {
        const quantity = selectedFeeQuantities[fee.id] || 0;
        return {
          fee_id: fee.id,
          name: fee.name,
          quantity,
          unit_price: Number(fee.price || 0),
          line_total: Number(fee.price || 0) * quantity,
        };
      })
      .filter((fee) => fee.quantity > 0);

  const getRegistrationTotal = (event = selectedEvent) =>
    getSelectedFeeLines(event).reduce((sum, fee) => sum + fee.line_total, 0);

  const getRegistrationPassCount = (event = selectedEvent) =>
    getSelectedFeeLines(event).reduce((sum, fee) => sum + fee.quantity, 0);

  const getRegistrationCurrency = (event = selectedEvent) =>
    String(event?.currency || 'USD').toUpperCase();

  const isRazorpayUnavailableForAmount = (event = selectedEvent) => {
    const maxMinorUnits = RAZORPAY_MAX_MINOR_UNITS[getRegistrationCurrency(event)];
    if (!maxMinorUnits) return false;
    return Math.round(getDelegateSubTotal() * 100) > maxMinorUnits;
  };

  const getRazorpayLimitMessage = (event = selectedEvent) => {
    const currency = getRegistrationCurrency(event);
    const maxMinorUnits = RAZORPAY_MAX_MINOR_UNITS[currency];
    if (!maxMinorUnits) return '';
    return `Razorpay supports up to ${currency} ${(maxMinorUnits / 100).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} per payment for this event. Please use Bank Transfer for larger amounts.`;
  };

  const razorpayUnavailableForAmount = isRazorpayUnavailableForAmount();
  const razorpayLimitMessage = getRazorpayLimitMessage();

  useEffect(() => {
    if (!showPaymentDialog) return;
    if (paymentMethod === 'card' && razorpayUnavailableForAmount) {
      setPaymentMethod('bank');
    }
  }, [showPaymentDialog, paymentMethod, razorpayUnavailableForAmount]);

  const getEventPriceLabel = (event) => {
    const delegateFees = getDelegateFeeOptions(event);
    if (delegateFees.length === 0) return `0 ${event?.currency || 'USD'}`;
    const prices = delegateFees.map((fee) => Number(fee.price || 0));
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);
    return minPrice === maxPrice
      ? `${minPrice} ${event?.currency || 'USD'}`
      : `From ${minPrice} ${event?.currency || 'USD'}`;
  };

  const getEventSponsorships = (event = selectedEvent) => {
    if (!event) return [];
    const list = event.sponsorships || event.sponsorship_packages || [];
    return list.filter((item) => item?.is_active !== false);
  };

  const getSponsorshipRemaining = (sponsorship) => {
    const available = Number(sponsorship?.quantity_available || 0);
    const sold = Number(sponsorship?.quantity_sold || sponsorship?.sold_count || 0);
    return Math.max(available - sold, 0);
  };

  const setSponsorshipQty = (sponsorshipId, nextQty, maxQty) => {
    const safeQty = Math.max(1, Math.min(Number(nextQty || 1), Number(maxQty || 1)));
    setSponsorshipSelections((prev) => ({ ...prev, [sponsorshipId]: safeQty }));
  };

  const addSponsorshipToCart = (sponsorship) => {
    const sponsorshipId = sponsorship?.sponsorship_id;
    if (!sponsorshipId) return;
    const remaining = getSponsorshipRemaining(sponsorship);
    if (remaining <= 0) {
      toast.error('This sponsorship is sold out.');
      return;
    }
    const selectedQty = Math.min(
      Math.max(Number(sponsorshipSelections[sponsorshipId] || 1), 1),
      remaining
    );

    setSponsorshipCart((prev) => {
      const existing = prev.find((item) => item.sponsorship_id === sponsorshipId);
      if (existing) {
        const maxAllowed = remaining;
        const mergedQty = Math.min(existing.quantity + selectedQty, maxAllowed);
        return prev.map((item) => (
          item.sponsorship_id === sponsorshipId
            ? { ...item, quantity: mergedQty }
            : item
        ));
      }
      return [...prev, {
        sponsorship_id: sponsorshipId,
        name: sponsorship.name || 'Sponsorship',
        type: sponsorship.type || 'sponsorship',
        unit_price: Number(sponsorship.price || 0),
        quantity: selectedQty,
        max_quantity: remaining,
      }];
    });
    toast.success('Sponsorship added to cart.');
  };

  const addDelegateToCart = (fee) => {
    const feeId = fee?.id;
    if (!feeId) return;
    const qty = Math.max(1, Number(selectedFeeQuantities[feeId] || 1));
    setDelegateCart((prev) => {
      const existing = prev.find((item) => item.fee_id === feeId);
      if (existing) {
        return prev.map((item) => (
          item.fee_id === feeId
            ? { ...item, quantity: Number(item.quantity || 0) + qty }
            : item
        ));
      }
      return [...prev, {
        fee_id: feeId,
        name: fee.name,
        unit_price: Number(fee.price || 0),
        quantity: qty,
      }];
    });
    setSelectedFeeQuantities((prev) => ({ ...prev, [feeId]: 1 }));
    toast.success(`${fee.name} added to cart`);
  };

  const updateDelegateCartQty = (feeId, nextQty) => {
    const qty = Math.max(1, Number(nextQty || 1));
    setDelegateCart((prev) => prev.map((item) => (
      item.fee_id === feeId ? { ...item, quantity: qty } : item
    )));
  };

  const removeDelegateCartItem = (feeId) => {
    setDelegateCart((prev) => prev.filter((item) => item.fee_id !== feeId));
  };

  const updateSponsorshipCartQty = (sponsorshipId, nextQty) => {
    setSponsorshipCart((prev) => prev.map((item) => {
      if (item.sponsorship_id !== sponsorshipId) return item;
      const qty = Math.max(1, Math.min(Number(nextQty || 1), Number(item.max_quantity || 1)));
      return { ...item, quantity: qty };
    }));
  };

  const removeSponsorshipCartItem = (sponsorshipId) => {
    setSponsorshipCart((prev) => prev.filter((item) => item.sponsorship_id !== sponsorshipId));
  };

  function getSponsorshipSubTotal() {
    return sponsorshipCart.reduce(
      (sum, item) => sum + (Number(item.unit_price || 0) * Number(item.quantity || 0)),
      0
    );
  }

  function getDelegateSubTotal() {
    return delegateCart.reduce(
      (sum, item) => sum + (Number(item.unit_price || 0) * Number(item.quantity || 0)),
      0
    );
  }
  const getUnifiedTotal = () => getSponsorshipSubTotal() + getDelegateSubTotal();
  const getCartItemCount = () =>
    delegateCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
    + sponsorshipCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const getDelegateFeeLinesFromCart = () => delegateCart.map((item) => ({
    fee_id: item.fee_id,
    name: item.name,
    quantity: Number(item.quantity || 0),
    unit_price: Number(item.unit_price || 0),
    line_total: Number(item.unit_price || 0) * Number(item.quantity || 0),
  }));
  const getDelegatePassCountFromCart = () => delegateCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const normalizedRegistrationStatus = String(myRegistration?.payment_status || '').toLowerCase();
  const hasBlockingRegistrationState = ['pending', 'processing', 'completed'].includes(normalizedRegistrationStatus);
  const canBuyDelegatePasses = !myRegistration || !hasBlockingRegistrationState;

  const handleSponsorshipPayNow = async () => {
    if (!selectedEvent || !currentMemberId) return;
    if (sponsorshipCart.length === 0 && delegateCart.length === 0) {
      toast.error('Cart is empty');
      return;
    }
    if (paymentMethod === 'card' && sponsorshipCart.length > 0) {
      toast.error('Online payment for mixed sponsorship cart is not available yet. Please use Bank Transfer.');
      setPaymentMethod('bank');
      return;
    }
    if (sponsorshipCart.length === 0) {
      if (paymentMethod === 'card') {
        await handleRegisterWithRazorpay();
      } else {
        await handleRegisterWithBank();
      }
      return;
    }
    setSponsorshipCheckoutLoading(true);
    try {
      const payload = {
        member_id: currentMemberId,
        company_id: member?.company_id || '',
        company_name: member?.company_name || '',
        payment_method: 'Bank Transfer',
        sponsorship_items: sponsorshipCart.map((item) => ({
          sponsorship_id: item.sponsorship_id,
          quantity: Number(item.quantity || 0),
        })),
        delegate_fee_lines: getDelegateFeeLinesFromCart(),
        delegate_count: getDelegatePassCountFromCart(),
      };
      const response = await eventsApi.checkoutSponsorships(selectedEvent.event_id, payload);
      setSponsorshipCheckout(response.data);
      setShowPaymentDialog(false);
      setShowSponsorshipBankDialog(true);
      setSponsorshipCart([]);
      setDelegateCart([]);
      toast.success('Sponsorship payment initiated.');
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Unable to initiate sponsorship payment'));
    } finally {
      setSponsorshipCheckoutLoading(false);
    }
  };

  const checkRegistration = async () => {
    if (!selectedEvent) return;
    try {
      const response = await eventsApi.checkRegistration(selectedEvent.event_id, currentMemberId);
      if (response.data.registered) {
        setMyRegistration(response.data.registration);
      } else {
        setMyRegistration(null);
      }
    } catch (error) {
      console.error('Error checking registration:', error);
      setMyRegistration(null);
    }
  };

  const fetchSponsorshipPayment = async () => {
    if (!selectedEvent || !currentMemberId) {
      setSponsorshipPendingPayment(null);
      return;
    }
    try {
      const response = await eventsApi.getMemberSponsorshipPayment(selectedEvent.event_id, currentMemberId);
      const payment = response?.data?.payment;
      if (!payment) {
        setSponsorshipPendingPayment(null);
        return;
      }
      const status = String(payment.status || '').toLowerCase();
      if (['pending', 'initiated', 'processing', 'proof uploaded', 'proof_uploaded', 'rejected'].includes(status)) {
        setSponsorshipPendingPayment(payment);
      } else {
        setSponsorshipPendingPayment(null);
      }
    } catch (error) {
      console.error('Error fetching sponsorship payment:', error);
      setSponsorshipPendingPayment(null);
    }
  };

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await eventsApi.getUpcomingEvents();
      setEvents(response.data);
      if (response.data.length > 0) {
        setSelectedEvent(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const response = await directoryApi.getMembers({ limit: 100 });
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchMeetingRequests = async () => {
    if (!selectedEvent) return;
    try {
      const response = await eventsApi.getMeetingRequests(selectedEvent.event_id, currentMemberId);
      setMeetingRequests(response.data);
    } catch (error) {
      console.error('Error fetching meeting requests:', error);
    }
  };

  const fetchScheduledMeetings = async () => {
    if (!selectedEvent) return;
    try {
      const response = await eventsApi.getScheduledMeetings(selectedEvent.event_id, currentMemberId);
      setScheduledMeetings(response.data);
    } catch (error) {
      console.error('Error fetching scheduled meetings:', error);
    }
  };

  const handleScheduleMeeting = async (e) => {
    e.preventDefault();
    try {
      await eventsApi.createMeetingRequest(selectedEvent.event_id, {
        request_id: '',
        event_id: selectedEvent.event_id,
        requester_id: currentMemberId,
        requested_member_id: scheduleForm.memberId,
        proposed_date: scheduleForm.date,
        proposed_time: scheduleForm.time,
        duration_minutes: scheduleForm.duration,
        message: scheduleForm.message,
        status: 'Pending'
      });
      toast.success('Meeting request sent successfully!');
      setShowScheduleDialog(false);
      setScheduleForm({ memberId: '', date: '', time: '', duration: 30, message: '' });
      fetchMeetingRequests();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to send meeting request'));
    }
  };

  const handleAcceptRequest = async (requestId) => {
    try {
      await eventsApi.acceptMeetingRequest(selectedEvent.event_id, requestId);
      toast.success('Meeting request accepted!');
      fetchMeetingRequests();
      fetchScheduledMeetings();
    } catch (error) {
      toast.error('Failed to accept request');
    }
  };

  const handleDeclineRequest = async (requestId) => {
    try {
      await eventsApi.declineMeetingRequest(selectedEvent.event_id, requestId);
      toast.info('Meeting request declined');
      fetchMeetingRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedMeeting) return;
    try {
      await eventsApi.updateMeeting(selectedEvent.event_id, selectedMeeting.meeting_id, {
        notes: meetingNotes.notes,
        todos: meetingNotes.todoItems
      });
      toast.success('Notes saved successfully!');
      setShowNotesDialog(false);
      fetchScheduledMeetings();
    } catch (error) {
      toast.error('Failed to save notes');
    }
  };

  const handleAddTodo = () => {
    if (newTodo.trim()) {
      setMeetingNotes({
        ...meetingNotes,
        todoItems: [...meetingNotes.todoItems, { id: Date.now(), text: newTodo, completed: false, assigned_to: '' }]
      });
      setNewTodo('');
    }
  };

  const handleRegisterForEvent = async () => {
    // Pre-fill GST details from member data
    setGstCompanyLegalName(member?.company_name || '');
    setGstUserName(member?.individual_name || user?.full_name || [user?.first_name, user?.last_name].filter(Boolean).join(' ') || '');
    setShowRegisterDialog(true);
  };

  const handlePayment = async (gateway) => {
    if (!selectedEvent || !currentMemberId) return;
    const companyId = member?.company_id || '';

    try {
      if (gateway === 'bank') {
        setBankTransferLoading(true);
        // Register with Bank Transfer: creates registration (Payment Pending) and emails admin
        await eventsApi.registerForEvent(selectedEvent.event_id, {
          event_id: selectedEvent.event_id,
          member_id: currentMemberId,
          company_id: companyId,
          registration_type: 'Delegate',
          payment_method: 'Bank Transfer',
          amount_paid: selectedEvent.delegate_fee ?? 0,
        });
        toast.success('Registration submitted. You will see Payment Pending until admin confirms payment.');
        setShowPaymentDialog(false);
        checkRegistration();
        return;
      }

      let response;
      const paymentData = {
        product_id: 'event_delegate',
        member_id: currentMemberId,
        company_id: companyId || 'COMP-001',
        reference_type: 'event_registration',
        reference_id: selectedEvent.event_id
      };

      if (gateway === 'stripe') {
        response = await paymentsApi.createStripeCheckout(paymentData);
        window.location.href = response.data.checkout_url;
      } else if (gateway === 'paypal') {
        response = await paymentsApi.createPayPalOrder(paymentData);
        window.location.href = response.data.approval_url;
      } else if (gateway === 'razorpay') {
        await handleRegisterWithRazorpay();
        return;
      }
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setBankTransferLoading(false);
    }
  };

  const isGstFormValid = () => {
    if (!isIndiaMember) return true;
    return Boolean(gstNumber.trim() && gstCompanyLegalName.trim() && gstUserName.trim());
  };

  const getGstPayload = () => {
    if (!isIndiaMember) return {};
    return {
      gst_number: gstNumber.trim(),
      company_legal_name: gstCompanyLegalName.trim(),
      user_name: gstUserName.trim(),
    };
  };

  const isBankFormValid = () => {
    const last4 = (payerAccountLast4 || '').replace(/\D/g, '');
    return (
      payerAccountName.trim() &&
      payerBankName.trim() &&
      last4.length === 4 &&
      transactionReference.trim() &&
      transferDate &&
      proofFile
    );
  };

  const handleProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload PDF, JPG, or PNG only.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB.');
      return;
    }
    setProofFile(file);
    setProofFileName(file.name);
  };

  const handleSponsorshipProofFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowed.includes(file.type)) {
      toast.error('Please upload PDF, JPG, or PNG only.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File must be under 10MB.');
      return;
    }
    setSponsorshipProofFile(file);
    setSponsorshipProofFileName(file.name);
  };

  const handleUploadSponsorshipProof = async () => {
    if (!selectedEvent || !currentMemberId || !sponsorshipPendingPayment?.payment_id) return;
    if (!sponsorshipProofFile) {
      toast.error('Please upload proof of transfer.');
      return;
    }
    setSponsorshipProofLoading(true);
    try {
      const uploadRes = await uploadsApi.uploadFile(
        sponsorshipProofFile,
        'payment_proof',
        selectedEvent.event_id,
        currentMemberId
      );
      const base = process.env.REACT_APP_BACKEND_URL || '';
      let proofUrl = uploadRes.data?.file_url || uploadRes.data?.url || uploadRes.data?.file?.url;
      if (proofUrl && proofUrl.startsWith('/')) proofUrl = base + proofUrl;

      await eventsApi.uploadSponsorshipPaymentProof(
        selectedEvent.event_id,
        sponsorshipPendingPayment.payment_id,
        {
          member_id: currentMemberId,
          bank_transfer_proof: proofUrl,
          transaction_reference: sponsorshipTransactionReference.trim() || undefined,
          transfer_date: sponsorshipTransferDate || undefined,
        }
      );
      toast.success('Sponsorship payment proof uploaded.');
      setShowSponsorshipProofDialog(false);
      setSponsorshipTransactionReference('');
      setSponsorshipTransferDate('');
      setSponsorshipProofFile(null);
      setSponsorshipProofFileName('');
      if (sponsorshipProofInputRef.current) sponsorshipProofInputRef.current.value = '';
      fetchSponsorshipPayment();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Failed to upload sponsorship proof'));
    } finally {
      setSponsorshipProofLoading(false);
    }
  };

  const fetchExistingRegistration = async () => {
    if (!selectedEvent || !currentMemberId) return null;
    const response = await eventsApi.checkRegistration(selectedEvent.event_id, currentMemberId);
    if (response.data?.registered) {
      const existingRegistration = response.data.registration;
      setMyRegistration(existingRegistration);
      return existingRegistration;
    }
    return null;
  };

  const openRazorpayPaymentLink = async (registrationId, totalAmount) => {
    const currency = selectedEvent?.currency || 'USD';
    const payerName = isIndiaMember
      ? gstUserName.trim()
      : (member?.individual_name || user?.full_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Member');
    const res = await paymentsApi.createEventPaymentLink({
      member_id: currentMemberId,
      event_id: selectedEvent.event_id,
      registration_id: registrationId,
      event_name: selectedEvent.title,
      amount: Math.round(totalAmount * 100),
      currency,
      ...getGstPayload(),
      customer: {
        name: payerName,
        email: user?.email || member?.email,
        contact: user?.phone || member?.phone || '',
      },
    });

    const shortUrl = res.data?.short_url;
    if (!shortUrl) {
      toast.error('Payment link could not be created. Please try again.');
      return;
    }

    const newWindow = window.open(shortUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) window.location.href = shortUrl;

    toast.success('Payment link opened. Complete the payment in Razorpay.');
    setShowPaymentDialog(false);
    setDelegateCart([]);
    checkRegistration();
  };

  const handleRegisterWithRazorpay = async () => {
    if (!selectedEvent || !currentMemberId || !isGstFormValid()) return;
    if (razorpayUnavailableForAmount) {
      setPaymentMethod('bank');
      toast.error(razorpayLimitMessage);
      return;
    }
    const selectedFees = getDelegateFeeLinesFromCart();
    if (selectedFees.length === 0) {
      toast.error('Select at least one pass before proceeding.');
      return;
    }
    const companyId = member?.company_id || '';
    const totalAmount = getDelegateSubTotal();
    setPaymentFormLoading(true);
    try {
      let existingRegistration = myRegistration;
      if (
        existingRegistration?.registration_id &&
        String(existingRegistration.payment_status || '').toLowerCase() === 'completed'
      ) {
        toast.error('Your registration is already completed for this event.');
        return;
      }
      if (!existingRegistration?.registration_id) {
        try {
          const regRes = await eventsApi.registerForEvent(selectedEvent.event_id, {
            event_id: selectedEvent.event_id,
            member_id: currentMemberId,
            company_id: companyId,
            registration_type: 'Delegate',
            selected_fees: selectedFees,
            total_passes: getDelegatePassCountFromCart(),
            payment_method: 'Razorpay',
            amount_paid: totalAmount,
            ...getGstPayload(),
          });
          existingRegistration = regRes.data;
        } catch (registrationError) {
          const detail = getErrorMessage(registrationError?.response?.data?.detail, '');
          if (!detail.toLowerCase().includes('already registered')) {
            throw registrationError;
          }

          existingRegistration = await fetchExistingRegistration();
          if (!existingRegistration?.registration_id) {
            throw registrationError;
          }
          if (String(existingRegistration.payment_status || '').toLowerCase() === 'completed') {
            toast.error('Your registration is already completed for this event.');
            return;
          }
        }
      }

      await openRazorpayPaymentLink(existingRegistration.registration_id, totalAmount);
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setPaymentFormLoading(false);
    }
  };

  const handleRegisterWithBank = async () => {
    if (!selectedEvent || !currentMemberId || !isBankFormValid()) {
      toast.error('Please fill all fields and upload proof of transfer.');
      return;
    }
    const selectedFees = getDelegateFeeLinesFromCart();
    if (selectedFees.length === 0) {
      toast.error('Select at least one pass before proceeding.');
      return;
    }
    const companyId = member?.company_id || '';
    setPaymentFormLoading(true);
    try {
      let proofUrl = null;
      if (proofFile) {
        const uploadRes = await uploadsApi.uploadFile(
          proofFile,
          'payment_proof',
          selectedEvent.event_id,
          currentMemberId
        );
        const base = process.env.REACT_APP_BACKEND_URL || '';
        proofUrl = uploadRes.data?.file_url || uploadRes.data?.url || uploadRes.data?.file?.url;
        if (proofUrl && proofUrl.startsWith('/')) proofUrl = base + proofUrl;
      }
      await eventsApi.registerForEvent(selectedEvent.event_id, {
        event_id: selectedEvent.event_id,
        member_id: currentMemberId,
        company_id: companyId,
        registration_type: 'Delegate',
        selected_fees: selectedFees,
        total_passes: getDelegatePassCountFromCart(),
        payment_method: 'Bank Transfer',
        amount_paid: getDelegateSubTotal(),
        payment_reference: transactionReference.trim() || undefined,
        bank_transfer_proof: proofUrl || undefined,
        payer_account_name: payerAccountName.trim(),
        payer_bank_name: payerBankName.trim(),
        payer_account_last4: payerAccountLast4.trim(),
        transaction_reference: transactionReference.trim(),
        transfer_date: transferDate,
      });
      toast.success('Registration submitted. You will see Payment Pending until admin confirms payment.');
      setShowPaymentDialog(false);
      setDelegateCart([]);
      checkRegistration();
    } catch (error) {
      toast.error(getErrorMessage(error.response?.data?.detail, 'Registration failed'));
    } finally {
      setPaymentFormLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getMemberName = (memberId) => {
    const member = members.find(m => m.member_id === memberId);
    return member ? `${member.individual_name} (${member.company_name})` : memberId;
  };

  if (hasRestriction('events')) {
    return <RestrictedOverlay message={getRestrictionMessage('events')} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading events...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Event Connect</h1>
        <p className="text-gray-600 mt-1">Schedule and manage meetings with members at events</p>
      </div>

      {/* Events Selection */}
      <Card>
        <CardHeader>
          <CardTitle>Upcoming Events</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No upcoming events</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {events.map((event) => (
                <div
                  key={event.event_id}
                  onClick={() => setSelectedEvent(event)}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${selectedEvent?.event_id === event.event_id
                    ? 'bg-blue-50 border-2 border-blue-500'
                    : 'bg-gray-50 border border-gray-200 hover:border-blue-300'
                    }`}
                  data-testid={`event-card-${event.event_id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <Calendar size={20} className="text-blue-600" />
                    <Badge className="bg-purple-100 text-purple-700">{event.status}</Badge>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-sm mb-2">{event.title}</h3>
                  <div className="flex items-center text-xs text-gray-600 mb-1">
                    <MapPin size={12} className="mr-1" />
                    {event.location}
                  </div>
                  <p className="text-xs text-gray-600 mb-2">
                    {formatDate(event.start_date)} - {formatDate(event.end_date)}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center text-xs text-gray-500">
                      <Users size={14} className="mr-1" />
                      {event.current_attendees}/{event.max_attendees}
                    </div>
                    <div className="flex items-center text-xs font-medium text-green-600">
                      <DollarSign size={12} />
                      {getEventPriceLabel(event)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {selectedEvent && (
        <>
          {/* Event Details & Actions */}
          <Card>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
                <div className="xl:col-span-6 border-b xl:border-b-0 xl:border-r border-gray-200 pb-4 xl:pb-0 xl:pr-5">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <Building2 size={28} className="text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-gray-900 leading-tight">{selectedEvent.title}</h2>
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-600">
                        <span className="flex items-center gap-2">
                          <MapPin size={17} />
                          {selectedEvent.location}
                        </span>
                        <span className="flex items-center gap-2">
                          <Calendar size={17} />
                          {formatDate(selectedEvent.start_date)}
                        </span>
                        <span className="flex items-center gap-2">
                          <Users size={17} />
                          {selectedEvent.current_attendees} attending
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">Delegate Passes</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {getDelegateFeeOptions(selectedEvent).map((fee) => (
                        <div key={fee.id} className="rounded-xl border border-gray-200 p-4 bg-white">
                          <p className="text-2xl font-medium text-gray-900">{fee.name}</p>
                          <p className="text-2xl font-bold text-gray-900 mt-2 leading-none">
                            ${Number(fee.price || 0).toLocaleString()}
                            <span className="text-lg font-semibold text-gray-500 ml-2">{selectedEvent.currency}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 text-gray-500 text-sm flex items-center gap-2">
                      <Info size={18} className="text-blue-500" />
                      Select a pass to register and attend the event.
                    </div>
                  </div>
                </div>

                <div className="xl:col-span-3 border-b xl:border-b-0 xl:border-r border-gray-200 pb-4 xl:pb-0 xl:pr-5">
                  <h3 className="text-xl font-semibold text-gray-900">Registration Status</h3>
                  <div className="mt-3">
                    {myRegistration?.payment_status === 'Completed' ? (
                      <Badge className="bg-green-100 text-green-700">Payment Completed</Badge>
                    ) : normalizedRegistrationStatus === 'pending' || normalizedRegistrationStatus === 'processing' ? (
                      <Badge className="bg-blue-100 text-blue-700">Payment Pending</Badge>
                    ) : normalizedRegistrationStatus === 'rejected' ? (
                      <Badge className="bg-red-100 text-red-700">Payment Rejected</Badge>
                    ) : (
                      <Badge className="bg-gray-100 text-gray-700">Not Registered</Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-3">
                    {myRegistration?.payment_status === 'Completed'
                      ? 'Registration completed. You can schedule meetings.'
                      : normalizedRegistrationStatus === 'pending' || normalizedRegistrationStatus === 'processing'
                        ? 'Admin will confirm payment and you will be notified by email.'
                        : normalizedRegistrationStatus === 'rejected'
                          ? 'Last transaction was rejected. Please start again.'
                          : 'Register and complete payment to schedule meetings.'}
                  </p>
                  <div className="mt-5 pt-5 border-t border-gray-200">
                    <h3 className="text-xl font-semibold text-gray-900">Sponsorship Status</h3>
                    <div className="mt-3">
                      {sponsorshipPendingPayment ? (
                        <Badge className={`${String(sponsorshipPendingPayment?.status || '').toLowerCase() === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                          {String(sponsorshipPendingPayment?.status || '').toLowerCase() === 'rejected'
                            ? 'Sponsorship Payment Rejected'
                            : 'Sponsorship Payment Pending'}
                        </Badge>
                      ) : (
                        <Badge className="bg-gray-100 text-gray-700">No Active Sponsorship Payment</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-3">
                      {sponsorshipPendingPayment
                        ? String(sponsorshipPendingPayment?.status || '').toLowerCase() === 'rejected'
                          ? 'Last sponsorship transaction was rejected. Start a new sponsorship checkout.'
                          : 'Upload payment proof after completing the bank transfer.'
                        : 'Add sponsorship packages from the list to get started.'}
                    </p>
                  </div>
                </div>

                <div className="xl:col-span-3 flex flex-col gap-3">
                  <Button
                    variant="outline"
                    className="h-11 text-base justify-center"
                    onClick={() => setShowSponsorshipDialog(true)}
                    disabled={getEventSponsorships(selectedEvent).length === 0}
                  >
                    <Building2 size={18} className="mr-2" />
                    {getEventSponsorships(selectedEvent).length > 0 ? 'Add Sponsorship' : 'No Sponsorships'}
                  </Button>
                  <Button
                    variant="outline"
                    className="h-11 text-base justify-center"
                    onClick={() => setShowPaymentDialog(true)}
                    disabled={getCartItemCount() === 0}
                  >
                    <ShoppingCart size={18} className="mr-2" />
                    Cart ({getCartItemCount()})
                  </Button>
                  {canBuyDelegatePasses && (
                    <Button
                      className="h-11 text-base bg-blue-600 hover:bg-blue-700 text-white justify-center"
                      onClick={handleRegisterForEvent}
                      data-testid="register-event-btn"
                    >
                      <CreditCard size={18} className="mr-2" />
                      Register Passes
                    </Button>
                  )}
                  {sponsorshipPendingPayment && String(sponsorshipPendingPayment?.status || '').toLowerCase() !== 'rejected' && (
                    <Button
                      variant="outline"
                      className="h-11 text-base border-amber-300 text-amber-700 hover:bg-amber-50 justify-center mt-3"
                      size="sm"
                      onClick={() => setShowSponsorshipProofDialog(true)}
                    >
                      <Upload size={18} className="mr-2" />
                      Upload Proof
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="py-4 text-gray-600 text-sm flex items-center gap-3">
              <Check className="text-blue-600" size={18} />
              Your registration and sponsorship help us create a better experience for everyone.
            </CardContent>
          </Card>

          {/* Tabs for Requests, Scheduled, Photos */}
          <Tabs defaultValue="requests">
            <TabsList>
              <TabsTrigger value="requests">
                Meeting Requests ({meetingRequests.filter(r => r.status === 'Pending').length})
              </TabsTrigger>
              <TabsTrigger value="scheduled">
                Scheduled ({scheduledMeetings.length})
              </TabsTrigger>
              <TabsTrigger value="photos">
                <Image size={14} className="mr-1" />
                Photos
              </TabsTrigger>
            </TabsList>

            <TabsContent value="requests" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  {meetingRequests.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No meeting requests</p>
                  ) : (
                    <div className="space-y-3">
                      {meetingRequests.map((request) => (
                        <div key={request.request_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <img
                              src={`https://api.dicebear.com/7.x/initials/svg?seed=${request.requester_id}`}
                              alt=""
                              className="w-10 h-10 rounded-full"
                            />
                            <div>
                              <p className="font-medium text-sm">
                                {request.requester_id === currentMemberId ?
                                  `You requested ${getMemberName(request.requested_member_id)}` :
                                  `${getMemberName(request.requester_id)} wants to meet`
                                }
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatDate(request.proposed_date)} at {request.proposed_time} - {request.duration_minutes} min
                              </p>
                              {request.message && (
                                <p className="text-xs text-gray-600 mt-1">{request.message}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={
                              request.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                                request.status === 'Accepted' ? 'bg-green-100 text-green-700' :
                                  'bg-red-100 text-red-700'
                            }>
                              {request.status}
                            </Badge>
                            {request.status === 'Pending' && request.requested_member_id === currentMemberId && (
                              <>
                                <Button size="sm" variant="outline" className="text-green-600" onClick={() => handleAcceptRequest(request.request_id)}>
                                  <Check size={14} />
                                </Button>
                                <Button size="sm" variant="outline" className="text-red-600" onClick={() => handleDeclineRequest(request.request_id)}>
                                  <X size={14} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="scheduled" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  {scheduledMeetings.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No scheduled meetings</p>
                  ) : (
                    <div className="space-y-3">
                      {scheduledMeetings.map((meeting) => (
                        <div key={meeting.meeting_id} className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Clock size={16} className="text-blue-600" />
                              <span className="font-medium">
                                {formatDate(meeting.meeting_date)} at {meeting.meeting_time}
                              </span>
                              <Badge variant="outline">{meeting.duration_minutes} min</Badge>
                            </div>
                            <Badge className={
                              meeting.status === 'Scheduled' ? 'bg-blue-100 text-blue-700' :
                                meeting.status === 'Completed' ? 'bg-green-100 text-green-700' :
                                  'bg-gray-100 text-gray-700'
                            }>
                              {meeting.status}
                            </Badge>
                          </div>
                          <div className="flex items-center space-x-2 mb-3">
                            <Users size={14} className="text-gray-500" />
                            <span className="text-sm text-gray-600">
                              {meeting.participants?.map(p => getMemberName(p)).join(', ')}
                            </span>
                          </div>
                          {meeting.location && (
                            <p className="text-sm text-gray-600 mb-2">
                              <MapPin size={14} className="inline mr-1" />
                              {meeting.location}
                            </p>
                          )}
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setSelectedMeeting(meeting);
                                setMeetingNotes({ notes: meeting.notes || '', todoItems: meeting.todos || [] });
                                setShowNotesDialog(true);
                              }}
                            >
                              <FileText size={14} className="mr-1" />
                              Notes & Todos
                            </Button>
                            <Button size="sm" variant="outline">
                              <MessageSquare size={14} className="mr-1" />
                              Chat
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="photos" className="mt-4">
              <Card>
                <CardContent className="p-4">
                  {(selectedEvent.photos || []).length === 0 ? (
                    <div className="text-center py-8">
                      <Image size={48} className="mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500">No photos yet</p>
                      <Button variant="outline" className="mt-4">
                        <Plus size={14} className="mr-1" />
                        Upload Photo
                      </Button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {selectedEvent.photos.map((photo, i) => (
                        <img key={i} src={photo} alt="" className="rounded-lg object-cover aspect-square" />
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Meeting Notes & Todos</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Notes</Label>
              <Textarea
                value={meetingNotes.notes}
                onChange={(e) => setMeetingNotes({ ...meetingNotes, notes: e.target.value })}
                placeholder="Meeting notes..."
                rows={4}
              />
            </div>
            <div>
              <Label>Todo Items</Label>
              <div className="flex space-x-2 mb-2">
                <Input
                  value={newTodo}
                  onChange={(e) => setNewTodo(e.target.value)}
                  placeholder="Add a task..."
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTodo()}
                />
                <Button onClick={handleAddTodo} variant="outline">
                  <Plus size={14} />
                </Button>
              </div>
              <div className="space-y-2">
                {meetingNotes.todoItems.map((todo, index) => (
                  <div key={todo.id || index} className="flex items-center space-x-2 p-2 bg-gray-50 rounded">
                    <input
                      type="checkbox"
                      checked={todo.completed}
                      onChange={() => {
                        const updated = [...meetingNotes.todoItems];
                        updated[index].completed = !updated[index].completed;
                        setMeetingNotes({ ...meetingNotes, todoItems: updated });
                      }}
                      className="rounded"
                    />
                    <span className={todo.completed ? 'line-through text-gray-400' : ''}>{todo.text}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowNotesDialog(false)}>Cancel</Button>
              <Button onClick={handleSaveNotes} className="bg-blue-600 hover:bg-blue-700 text-white">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRegisterDialog} onOpenChange={setShowRegisterDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Register Passes - {selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="sticky top-0 z-10 rounded-lg border bg-blue-50 p-4">
              <p className="text-sm text-blue-900 font-medium">Registration Summary</p>
              <p className="text-lg font-semibold text-blue-700 mt-1">
                Cart Total: ${Number(getDelegateSubTotal()).toLocaleString()} {selectedEvent?.currency || 'USD'}
              </p>
              <p className="text-sm text-blue-700">Items in cart: {delegateCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {getDelegateFeeOptions(selectedEvent).map((fee) => {
                const selectedQty = Math.max(1, Number(selectedFeeQuantities[fee.id] || 1));
                return (
                  <Card key={fee.id}>
                    <CardContent className="p-4 space-y-4">
                      <div>
                        <p className="font-semibold text-gray-900">{fee.name}</p>
                        <p className="text-sm text-gray-500">${Number(fee.price || 0).toLocaleString()} {selectedEvent?.currency || 'USD'} per pass</p>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateFeeQuantity(fee.id, selectedQty - 1)}
                          >
                            <Minus size={14} />
                          </Button>
                          <span className="w-8 text-center">{selectedQty}</span>
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() => updateFeeQuantity(fee.id, selectedQty + 1)}
                          >
                            <Plus size={14} />
                          </Button>
                        </div>
                        <Button
                          onClick={() => addDelegateToCart(fee)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
            <div className="sticky bottom-0 z-10 bg-white pt-2">
              <div className="flex justify-end gap-2 border-t pt-3">
                <Button variant="outline" onClick={() => setShowRegisterDialog(false)}>Continue Shopping</Button>
                <Button onClick={() => { setShowRegisterDialog(false); setShowPaymentDialog(true); }}>
                  View Cart ({getCartItemCount()})
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Unified Cart & Checkout Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => {
        if (!open) {
          setPaymentMethod('card');
          setGstNumber(''); setGstCompanyLegalName(''); setGstUserName('');
          setPayerAccountName(''); setPayerBankName(''); setPayerAccountLast4('');
          setTransactionReference(''); setTransferDate(''); setProofFile(null); setProofFileName('');
        }
        setShowPaymentDialog(open);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Cart & Checkout - {selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <p className="text-sm text-blue-900 font-medium">Amount Due</p>
                <p className="text-2xl font-bold text-blue-700 mt-1">
                  ${Number(getUnifiedTotal()).toLocaleString()} {selectedEvent?.currency || 'USD'}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  You are paying for: {getDelegatePassCountFromCart()} delegate pass(es), {sponsorshipCart.reduce((sum, item) => sum + Number(item.quantity || 0), 0)} sponsorship item(s)
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Delegate Registrations</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {delegateCart.length === 0 ? (
                  <p className="text-sm text-gray-500">No delegate passes in cart.</p>
                ) : delegateCart.map((item) => (
                  <div key={item.fee_id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">${Number(item.unit_price || 0).toLocaleString()} per pass</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" onClick={() => updateDelegateCartQty(item.fee_id, item.quantity - 1)}><Minus size={14} /></Button>
                      <span className="w-7 text-center">{item.quantity}</span>
                      <Button variant="outline" size="icon" onClick={() => updateDelegateCartQty(item.fee_id, item.quantity + 1)}><Plus size={14} /></Button>
                      <Button variant="ghost" className="text-red-600" onClick={() => removeDelegateCartItem(item.fee_id)}>Remove</Button>
                    </div>
                    <p className="font-medium">${Number(item.unit_price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Sponsorship Items</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {sponsorshipCart.length === 0 ? (
                  <p className="text-sm text-gray-500">No sponsorship items in cart.</p>
                ) : sponsorshipCart.map((item) => (
                  <div key={item.sponsorship_id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
                    <div>
                      <p className="font-medium text-gray-900">{item.name}</p>
                      <p className="text-xs text-gray-500">${Number(item.unit_price || 0).toLocaleString()} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="icon" onClick={() => updateSponsorshipCartQty(item.sponsorship_id, item.quantity - 1)}><Minus size={14} /></Button>
                      <span className="w-7 text-center">{item.quantity}</span>
                      <Button variant="outline" size="icon" onClick={() => updateSponsorshipCartQty(item.sponsorship_id, item.quantity + 1)}><Plus size={14} /></Button>
                      <Button variant="ghost" className="text-red-600" onClick={() => removeSponsorshipCartItem(item.sponsorship_id)}>Remove</Button>
                    </div>
                    <p className="font-medium">${Number(item.unit_price * item.quantity).toLocaleString()}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="card" disabled={razorpayUnavailableForAmount || sponsorshipCart.length > 0}>
                  <CreditCard size={16} className="mr-2" />
                  Pay Online
                </TabsTrigger>
                <TabsTrigger value="bank">
                  <Building2 size={16} className="mr-2" />
                  Bank Transfer
                </TabsTrigger>
              </TabsList>

              <TabsContent value="card" className="mt-4">
                <Card>
                  <CardContent className="space-y-5 p-6">
                    {razorpayUnavailableForAmount && (
                      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                        {razorpayLimitMessage}
                      </div>
                    )}
                    {isIndiaMember && (
                      <div className="space-y-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                        <div>
                          <p className="text-sm font-semibold text-amber-900">GST Details</p>
                          <p className="text-xs text-amber-700 mt-1">These details will be used on your tax invoice.</p>
                        </div>
                        <div className="grid gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="ec-gstNumber">GST Number</Label>
                            <Input
                              id="ec-gstNumber"
                              placeholder="Enter GST number"
                              value={gstNumber}
                              onChange={(e) => setGstNumber(e.target.value.toUpperCase())}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="ec-gstCompanyLegalName">Company Legal Name</Label>
                            <Input
                              id="ec-gstCompanyLegalName"
                              placeholder="Company legal name"
                              value={gstCompanyLegalName}
                              onChange={(e) => setGstCompanyLegalName(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="ec-gstUserName">Name of User</Label>
                            <Input
                              id="ec-gstUserName"
                              placeholder="Name of user"
                              value={gstUserName}
                              onChange={(e) => setGstUserName(e.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end gap-3 pt-2">
                      <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleSponsorshipPayNow}
                        disabled={
                          paymentFormLoading ||
                          !isGstFormValid() ||
                          delegateCart.length === 0 ||
                          razorpayUnavailableForAmount
                        }
                      >
                        {paymentFormLoading ? (
                          <><Loader2 size={16} className="mr-2 animate-spin" /> Processing...</>
                        ) : (
                          <><CreditCard size={16} className="mr-2" /> Pay ${Number(getUnifiedTotal()).toLocaleString()}</>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="bank" className="mt-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-lg">
                      <Building2 size={20} className="mr-2" />
                      Bank Transfer
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="space-y-2">
                      <Label className="font-semibold">Transfer To</Label>
                      <div className="rounded-lg border bg-muted/40 p-4 space-y-1.5 text-sm">
                        <p><span className="text-muted-foreground">Account Name:</span> {RECIPIENT_BANK.accountName}</p>
                        <p><span className="text-muted-foreground">Bank Name:</span> {RECIPIENT_BANK.bankName}</p>
                        <p className="pt-2 font-medium">USD (SWIFT)</p>
                        <p><span className="text-muted-foreground">Account No.:</span> {RECIPIENT_BANK.usd.accountNumber}</p>
                        <p><span className="text-muted-foreground">SWIFT Code:</span> {RECIPIENT_BANK.usd.swiftCode}</p>
                        <p className="pt-2 font-medium">INR (IFSC)</p>
                        <p><span className="text-muted-foreground">Account No.:</span> {RECIPIENT_BANK.inr.accountNumber}</p>
                        <p><span className="text-muted-foreground">IFSC Code:</span> {RECIPIENT_BANK.inr.ifscCode}</p>
                        <p className="font-medium text-blue-700 mt-2">Amount: ${Number(getUnifiedTotal()).toLocaleString()} {selectedEvent?.currency || 'USD'}</p>
                      </div>
                    </div>
                    <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 p-3 text-sm text-amber-800 dark:text-amber-200">
                      Please complete the bank transfer and submit the details below. Your payment will be verified within 24 hours.
                    </div>
                    <div className="grid gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="ec-payerAccountName">Your Account Name</Label>
                        <Input
                          id="ec-payerAccountName"
                          placeholder="Name on your bank account"
                          value={payerAccountName}
                          onChange={(e) => setPayerAccountName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ec-payerBankName">Your Bank Name</Label>
                        <Input
                          id="ec-payerBankName"
                          placeholder="Name of your bank"
                          value={payerBankName}
                          onChange={(e) => setPayerBankName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ec-payerAccountLast4">Your Account Number (last 4 digits)</Label>
                        <Input
                          id="ec-payerAccountLast4"
                          placeholder="****1234"
                          maxLength={4}
                          value={payerAccountLast4}
                          onChange={(e) => setPayerAccountLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ec-transactionReference">Transaction Reference Number</Label>
                        <Input
                          id="ec-transactionReference"
                          placeholder="e.g., TRF2025012345"
                          value={transactionReference}
                          onChange={(e) => setTransactionReference(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="ec-transferDate">Transfer Date</Label>
                        <Input
                          id="ec-transferDate"
                          type="date"
                          value={transferDate}
                          onChange={(e) => setTransferDate(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Upload Proof of Transfer</Label>
                        <div
                          className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30 p-6 cursor-pointer hover:bg-muted/50 transition-colors"
                          onClick={() => bankProofInputRef.current?.click()}
                        >
                          <input
                            ref={bankProofInputRef}
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            className="hidden"
                            onChange={handleProofFileChange}
                          />
                          <Upload size={24} className="text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">{proofFileName || 'Click to upload (PDF, JPG, PNG)'}</span>
                          <p className="text-xs text-muted-foreground">Upload a screenshot or PDF of your bank transfer receipt</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-2">
                      <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={handleSponsorshipPayNow}
                        disabled={paymentFormLoading || sponsorshipCheckoutLoading || !isBankFormValid() || getCartItemCount() === 0}
                      >
                        {paymentFormLoading ? (
                          <><Loader2 size={16} className="mr-2 animate-spin" /> Submitting...</>
                        ) : (
                          <><Building2 size={16} className="mr-2" /> Submit Transfer Details</>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSponsorshipDialog} onOpenChange={setShowSponsorshipDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Sponsorship Packages - {selectedEvent?.title}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {getEventSponsorships(selectedEvent).map((sponsorship) => {
              const remaining = getSponsorshipRemaining(sponsorship);
              const selectedQty = sponsorshipSelections[sponsorship.sponsorship_id] || 1;
              return (
                <Card key={sponsorship.sponsorship_id}>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-gray-900">{sponsorship.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{sponsorship.type}</p>
                      </div>
                      <Badge variant="outline">
                        {Number(sponsorship.quantity_sold || sponsorship.sold_count || 0)}/{Number(sponsorship.quantity_available || 0)} sold
                      </Badge>
                    </div>
                    <p className="text-lg font-bold text-blue-700">
                      ${Number(sponsorship.price || 0).toLocaleString()} {selectedEvent?.currency || 'USD'}
                    </p>
                    <p className="text-sm text-gray-600">Includes {Number(sponsorship.delegate_slots || 0)} delegates</p>
                    <p className="text-sm text-gray-600">+${Number(sponsorship.delegate_fee || 0).toLocaleString()} additional delegate fee</p>
                    <div className="flex flex-wrap gap-2">
                      {(sponsorship.benefits || []).map((benefit, idx) => (
                        <Badge key={`${sponsorship.sponsorship_id}-${idx}`} variant="secondary" className="text-xs">{benefit}</Badge>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setSponsorshipQty(sponsorship.sponsorship_id, selectedQty - 1, remaining)}
                          disabled={remaining <= 0}
                        >
                          <Minus size={14} />
                        </Button>
                        <span className="w-8 text-center">{selectedQty}</span>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => setSponsorshipQty(sponsorship.sponsorship_id, selectedQty + 1, remaining)}
                          disabled={remaining <= 0}
                        >
                          <Plus size={14} />
                        </Button>
                      </div>
                      <Button
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => addSponsorshipToCart(sponsorship)}
                        disabled={remaining <= 0}
                      >
                        Add to Cart
                      </Button>
                    </div>
                    <p className="text-xs text-gray-500">Remaining: {remaining}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSponsorshipBankDialog} onOpenChange={setShowSponsorshipBankDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Bank Transfer Instructions</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p><span className="font-medium">Bank Name:</span> {sponsorshipCheckout?.bank_details?.bank_name || '-'}</p>
            <p><span className="font-medium">Account Name:</span> {sponsorshipCheckout?.bank_details?.account_name || '-'}</p>
            <p><span className="font-medium">Account Number:</span> {sponsorshipCheckout?.bank_details?.account_number || '-'}</p>
            <p><span className="font-medium">IFSC/SWIFT:</span> {sponsorshipCheckout?.bank_details?.ifsc_or_swift || '-'}</p>
            <p><span className="font-medium">Reference Instructions:</span> {sponsorshipCheckout?.bank_details?.reference_instructions || '-'}</p>
            <p className="rounded-md bg-amber-50 border border-amber-200 p-3 text-amber-900">
              Payment record is created with status Initiated. Please complete bank transfer and share proof with admin.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSponsorshipProofDialog} onOpenChange={setShowSponsorshipProofDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Sponsorship Payment Proof</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Upload bank transfer proof for sponsorship payment ID: <span className="font-medium">{sponsorshipPendingPayment?.payment_id || '-'}</span>
            </div>
            <div>
              <Label>Transaction Reference (optional)</Label>
              <Input
                value={sponsorshipTransactionReference}
                onChange={(e) => setSponsorshipTransactionReference(e.target.value)}
                placeholder="UTR / bank reference"
              />
            </div>
            <div>
              <Label>Transfer Date (optional)</Label>
              <Input
                type="date"
                value={sponsorshipTransferDate}
                onChange={(e) => setSponsorshipTransferDate(e.target.value)}
              />
            </div>
            <div>
              <Label>Proof File (PDF/JPG/PNG) *</Label>
              <input
                ref={sponsorshipProofInputRef}
                type="file"
                accept=".pdf,image/jpeg,image/jpg,image/png"
                onChange={handleSponsorshipProofFileChange}
                className="block w-full text-sm text-gray-600 file:mr-4 file:rounded-md file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-blue-700 hover:file:bg-blue-100"
              />
              {sponsorshipProofFileName && (
                <p className="mt-1 text-xs text-gray-500">{sponsorshipProofFileName}</p>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowSponsorshipProofDialog(false)}>Cancel</Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleUploadSponsorshipProof}
                disabled={sponsorshipProofLoading || !sponsorshipProofFile}
              >
                {sponsorshipProofLoading ? <><Loader2 size={16} className="mr-2 animate-spin" />Uploading...</> : 'Upload Proof'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventConnect;
